// src/pages/ContactUs/ContactUs.js

import React, { useState } from 'react';
import {
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Link
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack'; // eslint-disable-line no-unused-vars
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SendIcon from '@mui/icons-material/Send';
import PremiumHeader from '../../components/Header/PremiumHeader';
import { useLang, t } from '../../context/LanguageContext';
import T from '../../translations/index';

import Footer from '../../components/Footer/Footer';
import ContactModal from './ContactModal';
import '../common.css';
import './contactus.css';

const ContactUs = () => {
  const navigate = useNavigate();
  const { lang } = useLang();
  const [modalOpen, setModalOpen] = useState(false);

  // eslint-disable-next-line no-unused-vars
  const handleNavClick = (e, path) => {
    e.preventDefault();
    
    switch (path) {
      case 'home':
        navigate('/');
        break;
      case 'about':
        navigate('/about');
        break;
      case 'services':
        navigate('/services');
        break;
      case 'projects':
        navigate('/projects');
        break;
      case 'clients':
        navigate('/clients');
        break;
      case 'careers':
        navigate('/careers');
        break;
      case 'hireus':
        navigate('/hireus');
        break;
      case 'contact':
        navigate('/contact');
        break;
      default:
        break;
    }
  };

  const contactInfo = [
    { 
      typeKey: 'emailType', 
      value: 'arunojha@trishivanay.com', 
      icon: <EmailIcon className="email-icon" />,
      descKey: 'emailDesc'
    },
    { 
      typeKey: 'phoneType', 
      value: '+91 6200247745', 
      icon: <PhoneIcon className="phone-icon" />,
      descKey: 'phoneDesc'
    },
    { 
      typeKey: 'whatsappType', 
      value: '+91 6200247745', 
      icon: <WhatsAppIcon className="whatsapp-icon" />, 
      link: 'https://wa.me/916200247745',
      descKey: 'whatsappDesc'
    }
  ];

  return (
    <div className="routed-page">
      <PremiumHeader />

      {/* Contact Hero */}
      <section className="contact-hero-banner">
        <div className="contact-hero-bg" />
        <div className="contact-hero-overlay" />
        <div className="contact-hero-content">
          <div className="contact-hero-eyebrow">Reach Out to Us</div>
          <h1 className="contact-hero-title">Get in <span>Touch With TCPL</span></h1>
          <p className="contact-hero-sub">We respond within 24 hours. Call, email, or drop us a WhatsApp message.</p>
        </div>
      </section>

      {/* Office Locations Strip */}
      <div className="contact-offices-strip">
        <div className="contact-offices-inner">
          {[
            { icon: '🏢', label: 'Registered Office', name: 'Trishivanay Construction Pvt. Ltd.', addr: 'Jamshedpur, Jharkhand, India' },
            { icon: '📞', label: 'Direct Contact', name: '+91 6200247745', addr: 'arunojha@trishivanay.com\nCall / WhatsApp Available' },
            { icon: '📍', label: 'Project Sites', name: 'Pan-India Presence', addr: 'Active projects in Jharkhand, Odisha, Chhattisgarh, Gujarat, West Bengal & more' },
          ].map((o, i) => (
            <div className="contact-office-card" key={i}>
              <span className="contact-office-icon">{o.icon}</span>
              <div className="contact-office-label">{o.label}</div>
              <div className="contact-office-name">{o.name}</div>
              <div className="contact-office-addr">{o.addr}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="page-container content-wrapper">
        <div className="contact-container">
        <Card className="contact-info-card">
          <CardContent>
            <Typography variant="h5" className="contact-section-title">
              {t(T.getInTouch, lang)}
            </Typography>
            <Typography variant="body2" className="contact-section-desc">
              {t(T.multipleWays, lang)}
            </Typography>
            
            <List className="contact-list">
              {contactInfo.map((info, idx) => (
                <ListItem key={idx} className="contact-list-item">
                  <div className="contact-item-icon">
                    {info.icon}
                  </div>
                  <ListItemText
                    primary={
                      info.typeKey === 'whatsappType' ? (
                        <Link 
                          href={info.link} 
                          target="_blank" 
                          rel="noopener" 
                          className="contact-link"
                        >
                          <strong>{t(T[info.typeKey], lang)}:</strong> {info.value}
                        </Link>
                      ) : (
                        <span className="contact-item-title">
                          <strong>{t(T[info.typeKey], lang)}:</strong> {info.value}
                        </span>
                      )
                    }
                    secondary={t(T[info.descKey], lang)}
                    className="contact-item-text"
                  />
                </ListItem>
              ))}
            </List>

            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              endIcon={<SendIcon />}
              onClick={() => setModalOpen(true)}
              className="open-contact-btn"
            >
              {t(T.sendUsMessage, lang)}
            </Button>
          </CardContent>
        </Card>

        <Card className="company-info-card">
          <CardContent>
            <Typography variant="h5" className="contact-section-title">
              {t(T.aboutOurCompany, lang)}
            </Typography>
            
            <div className="company-details">
              <div className="detail-block">
                <Typography variant="subtitle2" className="detail-title">
                  {t(T.expertiseTitle, lang)}
                </Typography>
                <Typography variant="body2" className="detail-desc">
                  {t(T.expertiseDesc, lang)}
                </Typography>
              </div>

              <div className="detail-block">
                <Typography variant="subtitle2" className="detail-title">
                  {t(T.servicesDetailTitle, lang)}
                </Typography>
                <Typography variant="body2" className="detail-desc">
                  {t(T.servicesDetailDesc, lang)}
                </Typography>
              </div>

              <div className="detail-block">
                <Typography variant="subtitle2" className="detail-title">
                  {t(T.capabilityTitle, lang)}
                </Typography>
                <Typography variant="body2" className="detail-desc">
                  {t(T.capabilityDesc, lang)}
                </Typography>
              </div>

              <div className="detail-block">
                <Typography variant="subtitle2" className="detail-title">
                  {t(T.qualityTitle, lang)}
                </Typography>
                <Typography variant="body2" className="detail-desc">
                  {t(T.qualityDesc, lang)}
                </Typography>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>

        <ContactModal open={modalOpen} onClose={() => setModalOpen(false)} />
      </div>

      {/* Map Section */}
      <section className="contact-map-section">
        <div className="contact-map-inner">
          <div className="contact-map-header">
            <div className="contact-map-eyebrow">Location</div>
            <h2 className="contact-map-title">Find Us — Jamshedpur, Jharkhand</h2>
          </div>
          <div className="contact-map-frame">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3670.986!2d86.2029!3d22.8046!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjLCsDQ4JzE2LjYiTiA4NsKwMTInMTAuNCJF!5e0!3m2!1sen!2sin!4v1234567890"
              title="TCPL Location — Jamshedpur"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactUs;
